import { useEffect, useRef, useState } from "react";
import type { ConnectorDTO } from "../../../shared/types";
import { Dialog } from "../../components/Dialog";
import { Table } from "../../components/Table";
import { TextLink } from "../../components/TextLink";
import { inputCls, Json, PageHeader, timeAgo } from "../../components/ui";
import { get } from "../../lib/api";
import { realtime } from "../../lib/ws";

export function AdminProxyLog() {
  const [rows, setRows] = useState<any[]>([]);
  const [connectors, setConnectors] = useState<ConnectorDTO[]>([]);
  const [connectorId, setConnectorId] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const query = `/api/proxy-calls?limit=300${connectorId ? `&connectorId=${connectorId}` : ""}${status ? `&status=${status}` : ""}`;
  const load = () => get<any[]>(query).then(setRows, () => {});

  useEffect(() => {
    get<ConnectorDTO[]>("/api/connectors").then(setConnectors, () => {});
  }, []);
  useEffect(() => {
    load();
    return realtime.subscribe("admin", (e) => {
      if (e.type !== "proxy.call") return;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(load, 250);
    });
  }, [query]);

  return (
    <div>
      <PageHeader title="Proxy log" subtitle="Every connector call made by agents or admins, with bodies (64 KB max, secrets redacted)." />
      <div className="flex flex-col gap-2 px-4 pt-4 sm:flex-row md:px-6">
        <select className={`${inputCls} sm:w-48`} value={connectorId} onChange={(e) => setConnectorId(e.target.value)}>
          <option value="">All connectors</option>
          {connectors.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select className={`${inputCls} sm:w-36`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>
          <option value="ok">OK (&lt;400)</option>
          <option value="error">Errors</option>
        </select>
      </div>
      <div className="px-4 py-4 md:px-6">
        <Table
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => get(`/api/proxy-calls/${r.id}`).then(setSelected)}
          empty="No calls yet."
          columns={[
            { header: "When", className: "text-xs text-muted", cell: (r) => timeAgo(r.created_at) },
            { header: "Connector", cell: (r) => r.connector_slug ?? "—" },
            { header: "Agent", className: "text-muted", cell: (r) => r.agent_slug ?? "admin" },
            { header: "Scope", className: "font-mono text-xs text-muted", cell: (r) => r.scope_key ?? "—" },
            {
              header: "Request",
              primary: true,
              cell: (r) => <div className="truncate font-mono text-xs">{r.method} {r.path}{r.query}</div>,
            },
            { header: "Status", cell: (r) => <span className={r.status >= 400 || r.error ? "text-danger" : "text-success"}>{r.status}</span> },
            { header: "Time", className: "text-xs text-muted", cell: (r) => `${r.duration_ms}ms` },
            { header: "Size", className: "text-xs text-muted", cell: (r) => `${r.response_bytes}b` },
          ]}
        />
      </div>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} size="xl" title={selected && `${selected.method} ${selected.path}`}>
        {selected && (
          <div className="space-y-3 text-sm">
            <div className="font-mono text-xs break-all">
              {selected.method} {selected.path}{selected.query} → <b>{selected.status}</b> in {selected.duration_ms}ms
              {selected.scope_key && <span className="text-muted"> · scope {selected.scope_key}</span>}
              {selected.pass_sub && <span className="text-muted"> · pass for {selected.pass_sub} ({selected.pass_jti})</span>}
            </div>
            {selected.run_id && (
              <TextLink to={`/admin/runs/${selected.run_id}`} className="text-xs">View run</TextLink>
            )}
            {selected.error && <p className="text-danger">{selected.error}</p>}
            <div>
              <div className="mb-1 text-xs font-medium text-muted">Request body ({selected.request_bytes} bytes)</div>
              <Json value={selected.request_body ?? "(empty)"} className="max-h-60 whitespace-pre-wrap" />
            </div>
            <div>
              <div className="mb-1 text-xs font-medium text-muted">Response body ({selected.response_bytes} bytes)</div>
              <Json value={selected.response_body ?? "(empty)"} className="max-h-[50vh] whitespace-pre-wrap" />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
